import React, {useCallback} from 'react';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';
import {useTranslation} from 'react-i18next';

import {useAuthStore} from '@/entities/session/auth-store';
import type {PeopleStackParamList} from '@/app/routes';
import {PeopleList} from '@/features/view-people/ui/PeopleList';
import {PersonCard} from '@/features/view-people/ui/PersonCard';
import {Page} from '@/shared/ui/page';
import {ContextPlaceholder} from '@/widgets/context-placeholder/ContextPlaceholder';

/**
 * «Люди» личного пространства. Корень стека: нажатие на строке открывает карточку
 * только на чтение — заводить и править людей в личном пространстве нечем, они
 * приезжают синхронизацией с терминала.
 */
type Props = NativeStackScreenProps<PeopleStackParamList, 'PeopleList'>;

export function PeopleScreen({navigation}: Props) {
  const {t} = useTranslation();
  const companyId = useAuthStore(s => s.activeContext?.companyId);

  // Стабильная ссылка, объявленная ДО раннего возврата ниже: хук после условного
  // return — это хук, который иногда не выполняется. Список отдаёт её мемоизированным
  // строкам, и свежая стрелка тут перерисовывала бы всех видимых людей на каждый
  // рендер экрана.
  const openPerson = useCallback(
    (recordId: string, name: string) => navigation.navigate('Person', {recordId, name}),
    [navigation],
  );

  if (!companyId) {
    return <ContextPlaceholder titleKey="nav.people" />;
  }

  /*
    Прокрутка одна и принадлежит странице на любой ширине: список — постраничный, а
    не виртуализированный, и без страничной полосы двадцать пять карточек на телефоне
    просто не долистывались.
  */
  return (
    <Page scroll gutter={false} title={t('nav.people')} testID="people-screen">
      <PeopleList
        companyId={companyId}
        onOpen={openPerson}
        // На широком окне карточка открывается РЯДОМ с таблицей, а не вместо неё.
        renderDetail={recordId => <PersonCard companyId={companyId} recordId={recordId} />}
      />
    </Page>
  );
}
